/**
 * JR STAFF PARITY, browser half.
 *
 * Bobbie, 2026-08-19: "the dancer one when promoted should literally work
 * the same way as jr staff with their own log in!" And 2026-08-21: "dancer
 * accounts that are flagged as junior staff... need the exact same access as
 * someone who created a junior staff account who is also a dancer."
 *
 * A dancer sign-in is a server cookie with no Firebase principal, so the
 * rules deny it everywhere and the staff surfaces cannot open. When a
 * Director designates that dancer Jr Staff, the server promotes their
 * users/{uid} doc to the SAME shape an adult Jr Staff account has (role
 * 'ensemble', status 'active') and hands the browser a Firebase custom token
 * for the same uid. This file is where that token becomes a session.
 *
 * TWO SESSIONS AT ONCE, ON PURPOSE. The dancer keeps their cookie AND gains
 * a Firebase session, because they are one person with two surfaces: the
 * dancer portal is still theirs, and the staff app is now theirs too. Every
 * other place in this app treats a Firebase user as proof that an adult is
 * signed in and clears any dancer cookie underneath it; the ONE exception is
 * a principal carrying the `jrStaffDancer` claim, which only the dancer
 * endpoint mints and only for the uid the cookie already authenticates.
 *
 * The claim is read from the ID token rather than the users doc so the
 * carve-out cannot be reached by writing a Firestore document: a client can
 * never set its own custom claims.
 */
import { signInWithCustomToken, type User } from 'firebase/auth';
import { auth } from './firebase';

/** The claim netlify/functions/dancerAuth.mjs sets, and nothing else does. */
export const JR_STAFF_DANCER_CLAIM = 'jrStaffDancer';

/**
 * Is this Firebase user a promoted Jr Staff dancer, rather than an adult
 * account? Fails CLOSED: an unreadable token answers false, which routes the
 * caller down the ordinary adult path instead of granting the carve-out.
 */
export async function isJrStaffDancerPrincipal(user: User | null | undefined): Promise<boolean> {
  return (await readJrStaffDancerClaim(user)) === true;
}

/**
 * The same question with "could not tell" kept apart from "no" (B15,
 * 2026-10-25). NULL means the ID token could not be read: a saved token past
 * its hour that cannot be refreshed right now, which is exactly the
 * home-screen app opened after a while with the network not up yet.
 *
 * Use this, not the boolean above, wherever "no" leads to DESTROYING
 * something. The dancer session provider used to read an unreadable claim as
 * "adult" and log the dancer cookie out on the server, so a promoted Jr Staff
 * lost her sign-in to a network blip. Null opens no door either: every gate
 * still needs a true.
 */
export async function readJrStaffDancerClaim(user: User | null | undefined): Promise<boolean | null> {
  if (!user) return false;
  try {
    const result = await user.getIdTokenResult();
    return result.claims?.[JR_STAFF_DANCER_CLAIM] === true;
  } catch {
    return null;
  }
}

/**
 * Exchange the minted token for a Firebase session. Returns false rather
 * than throwing on every failure: a dancer whose staff session could not
 * start must stay signed in to their own portal, which needs no Firebase
 * token at all. They simply do not see the staff surfaces this load.
 */
export async function signInAsJrStaffDancer(token: string): Promise<boolean> {
  if (!token || !auth) return false;
  try {
    await signInWithCustomToken(auth, token);
    return true;
  } catch (error) {
    console.warn('Jr Staff dancer sign-in could not start', error);
    return false;
  }
}
